import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { useToast } from "@/contexts/ToastContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import {
  Plus,
  Edit2,
  Trash2,
  ClipboardCheck,
  Eye,
  EyeOff,
  ShieldOff,
  GripVertical,
  Layers,
} from "lucide-react";
import { bookConditionsApi, type BookCondition } from "@/lib/api/book-conditions";
import { useBookConditions, BOOK_CONDITIONS_QUERY_KEY } from "@/hooks/useBookConditions";
import { cn } from "@/lib/utils";

const BookConditionsSettingsPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isTenantAdmin } = useAccessControl();
  const [includeInactive, setIncludeInactive] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  const { data: allBookConditions = [], isLoading: loading } = useBookConditions(true, {
    enabled: isTenantAdmin,
  });
  const bookConditions = [
    ...(includeInactive ? allBookConditions : allBookConditions.filter((c) => c.isActive)),
  ].sort((a, b) => a.order - b.order);
  const stats = {
    active: allBookConditions.filter((c) => c.isActive).length,
    inactive: allBookConditions.filter((c) => !c.isActive).length,
    total: allBookConditions.length,
  };

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [conditionToDelete, setConditionToDelete] = useState<BookCondition | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Drag and drop state
  const [draggedItem, setDraggedItem] = useState<BookCondition | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragNodeRef = useRef<HTMLDivElement | null>(null);

  const handleEdit = (condition: BookCondition) => {
    navigate(`/setup/book-conditions/${condition.id}/edit`);
  };

  const openDeleteDialog = (condition: BookCondition) => {
    setConditionToDelete(condition);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!conditionToDelete) return;

    try {
      setDeleting(true);
      const hardDelete = includeInactive || !conditionToDelete.isActive;
      await bookConditionsApi.delete(conditionToDelete.id, hardDelete);

      const actionWord = hardDelete ? "permanently deleted" : "deactivated";
      toast.success(`"${conditionToDelete.label}" has been ${actionWord}.`);
      queryClient.invalidateQueries(BOOK_CONDITIONS_QUERY_KEY);
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Unable to delete condition. Please try again.");
    } finally {
      setDeleting(false);
      setConditionToDelete(null);
    }
  };

  const handleNewClick = () => {
    navigate("/setup/book-conditions/new");
  };

  // Drag and drop handlers
  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    condition: BookCondition,
    index: number
  ) => {
    setDraggedItem(condition);
    dragNodeRef.current = e.currentTarget;

    // Set drag image and data
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", condition.id);

    // Add a small delay to apply the dragging style
    setTimeout(() => {
      if (dragNodeRef.current) {
        dragNodeRef.current.classList.add("opacity-50");
      }
    }, 0);
  };

  const handleDragEnd = () => {
    if (dragNodeRef.current) {
      dragNodeRef.current.classList.remove("opacity-50");
    }
    setDraggedItem(null);
    setDragOverIndex(null);
    dragNodeRef.current = null;
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";

    if (draggedItem && dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    // Only reset if we're leaving the container entirely
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!e.currentTarget.contains(relatedTarget)) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
    e.preventDefault();

    if (!draggedItem) return;

    const dragIndex = bookConditions.findIndex((c) => c.id === draggedItem.id);

    if (dragIndex === dropIndex) {
      handleDragEnd();
      return;
    }

    // Reorder the array
    const newConditions = [...bookConditions];
    const [removed] = newConditions.splice(dragIndex, 1);
    newConditions.splice(dropIndex, 0, removed);
    const orderedIds = newConditions.map((c) => c.id);
    handleDragEnd();

    // Save the new order to the server
    try {
      setIsSavingOrder(true);
      await bookConditionsApi.reorder(orderedIds);
      toast.success("Order updated successfully!");
      queryClient.invalidateQueries(BOOK_CONDITIONS_QUERY_KEY);
    } catch (error: any) {
      console.error("Failed to save order:", error);
      toast.error("Failed to save the new order. Please try again.");
      queryClient.invalidateQueries(BOOK_CONDITIONS_QUERY_KEY);
    } finally {
      setIsSavingOrder(false);
    }
  };

  // Filter conditions (only when not searching, so drag-and-drop works correctly)
  const displayConditions = searchQuery
    ? bookConditions.filter(
        (condition) =>
          condition.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
          condition.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
          condition.description?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : bookConditions;

  const isDragDisabled = !!searchQuery || isSavingOrder;

  if (!isTenantAdmin) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <Card className="max-w-md p-8 text-center">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
              <ShieldOff className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="mb-2 text-xl font-bold text-gray-900">Access Denied</h2>
            <p className="text-gray-600">
              You need administrator permissions to manage book conditions.
            </p>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageContainer width="wide">
        <PageHeader
          title="Book Conditions"
          description="Define physical condition labels for library materials"
          breadcrumbItems={[{ label: "Setup", href: "/setup" }, { label: "Book Conditions" }]}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search by code or label..."
        />

        {/* Quick Info Cards */}
        <div className="grid grid-cols-1 gap-4 mb-6 md:grid-cols-3">
          <Card className="p-4 bg-white border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <ClipboardCheck className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
                <p className="text-sm text-gray-500">Active Conditions</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-50">
                <EyeOff className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.inactive}</p>
                <p className="text-sm text-gray-500">Inactive Conditions</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-teal-50">
                <Layers className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                <p className="text-sm text-gray-500">Total Conditions</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Actions Bar */}
        <div className="flex flex-col justify-between gap-4 mb-6 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <Checkbox
              id="includeInactive"
              checked={includeInactive}
              onCheckedChange={(checked) => setIncludeInactive(checked === true)}
            />
            <Label
              htmlFor="includeInactive"
              className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer"
            >
              {includeInactive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              Show inactive conditions
            </Label>
          </div>
          <Button onClick={handleNewClick} className="shadow-sm">
            <Plus className="w-4 h-4 mr-2" />
            Add Condition
          </Button>
        </div>

        {/* Conditions List */}
        <Card className="overflow-hidden bg-white shadow-sm">
          <div className="px-5 py-4 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">All Conditions</h3>
              {!searchQuery && (
                <p className="text-xs text-gray-500">
                  <GripVertical className="inline-block w-3 h-3 mr-1" />
                  Drag items to reorder
                </p>
              )}
            </div>
          </div>

          {loading ? (
            <div className="py-16">
              <LoadingSpinner size="lg" label="Loading conditions..." />
            </div>
          ) : displayConditions.length === 0 ? (
            <div className="py-16 text-center">
              <div className="flex items-center justify-center w-16 h-16 p-4 mx-auto mb-4 bg-gray-100 rounded-full">
                <ClipboardCheck className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900">
                {searchQuery ? "No matching conditions" : "No conditions yet"}
              </h3>
              <p className="max-w-sm mx-auto mb-6 text-sm text-gray-500">
                {searchQuery
                  ? "Try a different search term or check for typos."
                  : "Create your first condition to track the physical state of books."}
              </p>
              {!searchQuery && (
                <Button onClick={handleNewClick} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Condition
                </Button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {displayConditions.map((condition, index) => (
                <div
                  key={condition.id}
                  draggable={!isDragDisabled}
                  onDragStart={(e) => handleDragStart(e, condition, index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  className={cn(
                    "p-5 transition-all select-none",
                    !condition.isActive && "bg-gray-50/50",
                    !isDragDisabled && "cursor-grab active:cursor-grabbing",
                    isDragDisabled && "cursor-default",
                    draggedItem?.id === condition.id && "opacity-50",
                    dragOverIndex === index &&
                      draggedItem?.id !== condition.id &&
                      "bg-primary/5 border-t-2 border-primary"
                  )}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div
                        className={cn(
                          "flex items-center justify-center w-8 h-8 text-gray-400 transition-colors",
                          !isDragDisabled && "hover:text-gray-600"
                        )}
                      >
                        <GripVertical className="w-5 h-5" />
                      </div>
                      <div className="flex items-center justify-center w-8 h-8 text-sm font-medium text-gray-500 bg-gray-100 rounded">
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h3 className="text-lg font-semibold text-gray-900">{condition.label}</h3>
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-mono rounded">
                            {condition.code}
                          </span>
                          <span
                            className={cn(
                              "px-2.5 py-0.5 rounded-full text-xs font-medium",
                              condition.isActive
                                ? "bg-green-100 text-green-700"
                                : "bg-gray-200 text-gray-600"
                            )}
                          >
                            {condition.isActive ? "Active" : "Inactive"}
                          </span>
                        </div>
                        {condition.description && (
                          <p className="text-sm text-gray-500">{condition.description}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(condition)}
                        className="gap-1.5"
                      >
                        <Edit2 className="w-4 h-4" />
                        <span className="hidden sm:inline">Edit</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openDeleteDialog(condition)}
                        className="gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-200"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span className="hidden sm:inline">
                          {condition.isActive ? "Deactivate" : "Delete"}
                        </span>
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {isSavingOrder && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/80">
              <LoadingSpinner size="md" label="Saving order..." />
            </div>
          )}
        </Card>

        {/* Help Text */}
        <div className="p-4 mt-6 bg-white border border-gray-200 rounded-lg">
          <h4 className="mb-1 font-medium text-gray-900">What are book conditions?</h4>
          <p className="text-sm text-gray-600">
            Book conditions describe the physical state of library materials. Common examples
            include "New," "Good," "Fair," "Poor," or "Damaged." These labels help track item
            quality and can be used when cataloging or during check-in inspections. Drag items to
            change their display order in dropdown menus.
          </p>
        </div>
      </PageContainer>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={
          conditionToDelete?.isActive
            ? `Deactivate "${conditionToDelete?.label}"?`
            : `Delete "${conditionToDelete?.label}"?`
        }
        description={
          conditionToDelete?.isActive ? (
            <>
              <p className="mb-2">
                Deactivating this condition will hide it from selection lists. Existing items with
                this condition will not be affected.
              </p>
              <p className="font-medium text-gray-600">You can reactivate it later if needed.</p>
            </>
          ) : (
            <>
              <p className="mb-2">
                This will <strong>permanently delete</strong> this condition. This action cannot be
                undone.
              </p>
              <p className="font-medium text-red-600">
                Make sure no items are currently using this condition.
              </p>
            </>
          )
        }
        confirmLabel={conditionToDelete?.isActive ? "Deactivate" : "Delete Permanently"}
        cancelLabel="Cancel"
        onConfirm={handleDelete}
        variant="danger"
        loading={deleting}
      />
    </AppLayout>
  );
};

export default BookConditionsSettingsPage;
